import React, { useEffect, useState } from 'react';
import { testimonialService } from '../services/testimonialService';
import { ITestimonial } from '../types/testimonial.types';
import '../styles/testimonials-public.css';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') ?? 'http://localhost:8000';

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
    <div className="tp-stars" aria-label={`${rating} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map(i => (
            <span key={i} className={`tp-star${i <= rating ? ' tp-star--filled' : ''}`}>★</span>
        ))}
    </div>
);

const TestimonialCard: React.FC<{ testimonial: ITestimonial }> = ({ testimonial: t }) => (
    <div className="tp-card">
        <div className="tp-quote-icon" aria-hidden="true">"</div>
        {t.rating != null && <StarRating rating={t.rating} />}
        <p className="tp-text">{t.testimonialText}</p>
        <div className="tp-client">
            {t.clientImageUrl ? (
                <img
                    className="tp-avatar"
                    src={`${BASE_URL}${t.clientImageUrl}`}
                    alt={t.clientName}
                    loading="lazy"
                />
            ) : (
                <div className="tp-avatar-ph" aria-hidden="true">
                    {t.clientName.charAt(0).toUpperCase()}
                </div>
            )}
            <div className="tp-client-info">
                <strong className="tp-client-name">{t.clientName}</strong>
                {t.clientTitle && <span className="tp-client-title">{t.clientTitle}</span>}
            </div>
        </div>
    </div>
);

const SkeletonCard: React.FC = () => (
    <div className="tp-card tp-card--skeleton">
        <div className="tp-sk-line tp-sk-line--short" />
        <div className="tp-sk-line" />
        <div className="tp-sk-line" />
        <div className="tp-sk-line tp-sk-line--medium" />
        <div className="tp-sk-client">
            <div className="tp-sk-avatar" />
            <div className="tp-sk-client-text">
                <div className="tp-sk-line tp-sk-line--name" />
                <div className="tp-sk-line tp-sk-line--role" />
            </div>
        </div>
    </div>
);

export const TestimonialsPublic: React.FC = () => {
    const [testimonials, setTestimonials] = useState<ITestimonial[]>([]);
    const [loading,      setLoading]      = useState(true);

    useEffect(() => {
        testimonialService.fetchPublic()
            .then(({ data }) => {
                setTestimonials(data.data);
                setLoading(false);
            })
            .catch(() => {
                setTestimonials([]);
                setLoading(false);
            });
    }, []);

    if (!loading && testimonials.length === 0) return null;

    return (
        <section className="tp-section" aria-label="Client Testimonials">

            <div className="tp-top-bar" aria-hidden="true" />

            <div className="container">

                <div className="tp-header">
                    <span className="tp-eyebrow">TESTIMONIALS</span>
                    <h2 className="tp-title">
                        What Our Clients Say
                        <span className="tp-title-underline" />
                    </h2>
                    <p className="tp-subtitle">
                        Trusted by artisans and buyers across India and beyond
                    </p>
                </div>

                <div className="tp-grid">
                    {loading
                        ? [1, 2, 3].map(i => <SkeletonCard key={i} />)
                        : testimonials.map(t => <TestimonialCard key={t._id} testimonial={t} />)
                    }
                </div>

            </div>
        </section>
    );
};
